import { useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
  Animated,
  Easing,
} from "react-native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { Package } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import StatusBadge from "../components/StatusBadge";
import { EMBER } from "../../onboarding/theme";
import type { AccessStackParamList } from "../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP, HEADER_INSET } from "@louise/moon-ui/theme/tokens";
import ScreenHeader from "../components/ScreenHeader";
import { useTranslations, useLocale } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { getBlocksByKind } from "@src/packages/content";
import FindCareScreen from "./access/FindCareScreen";
import ClinicDetailScreen from "./access/ClinicDetailScreen";
import TestingScreen from "./access/TestingScreen";
import AmhComparisonScreen from "./access/AmhComparisonScreen";
import TestPanelComparisonScreen from "./access/TestPanelComparisonScreen";
import PgtComparisonScreen from "./access/PgtComparisonScreen";
import EndometrialAssayComparisonScreen from "./access/EndometrialAssayComparisonScreen";
import ProgramsScreen from "./access/ProgramsScreen";
import SupplementsScreen from "./access/SupplementsScreen";
import DonorRegistryScreen from "./access/DonorRegistryScreen";
import TherapiesScreen from "./access/TherapiesScreen";
import TestDetailScreen from "./access/TestDetailScreen";
import ProgramDetailScreen from "./access/ProgramDetailScreen";
import ConditionDetailScreen from "./access/ConditionDetailScreen";
import SupplementDetailScreen from "./access/SupplementDetailScreen";
import TherapyDetailScreen from "./access/TherapyDetailScreen";
import NetworkLocationsScreen from "./access/NetworkLocationsScreen";
import PharmacyScreen from "./access/PharmacyScreen";
import PharmacyDetailScreen from "./access/PharmacyDetailScreen";

const COPPER_TEXTURE = require("../../assets/images/dark-copper-texture.png");
const COPPER_TEXTURE_2 = require("../../assets/images/copper-texture-2.png");
const COPPER_TEXTURE_3 = require("../../assets/images/copper-texture-3.png");
const GOLD_TEXTURE = require("../../assets/images/gold-texture.png");

const TEXTURE_MAP = {
  copper: COPPER_TEXTURE,
  copper2: COPPER_TEXTURE_2,
  copper3: COPPER_TEXTURE_3,
  gold: GOLD_TEXTURE,
} as const;

const Stack = createNativeStackNavigator<AccessStackParamList>();

/* ─── Category Card Data ─── */

/**
 * Each card gets a unique abstract shape — the dark copper texture clipped
 * via CSS clip-path on web (circle, triangle, star, hexagon, etc.) and
 * borderRadius fallback on native. Anchored at top-right corner, partially
 * cropped by overflow:hidden.
 */
type ShapeConfig = {
  size: number;
  /** Native fallback border-radius */
  radius: number;
  top: number;
  right: number;
  /** CSS clip-path polygon (web only) */
  clipPath?: string;
  rotate?: number;
  /** Which texture to use */
  texture?: "copper" | "copper2" | "copper3" | "gold";
};

type CategoryCard = {
  title: string;
  subtitle: string;
  /** i18n key of `title`, kept stable for analytics across locales. */
  action: string;
  texture: NonNullable<ShapeConfig["texture"]>;
  comingSoon?: boolean;
  statusBadge?: "beta" | "coming-soon";
  route: Exclude<
    keyof AccessStackParamList,
    | "AccessOverview"
    | "ClinicDetail"
    | "TestComparison"
    | "TestPanelComparison"
    | "PgtComparison"
    | "EndometrialAssayComparison"
    | "TestDetail"
    | "TestProductDetail"
    | "ProgramDetail"
    | "ConditionDetail"
    | "SupplementDetail"
    | "TherapyDetail"
    | "NetworkLocations"
    | "PharmacyDetail"
  >;
};

const CATEGORY_SHAPE: Omit<ShapeConfig, "texture"> = {
  size: 120,
  radius: 60,
  top: -30,
  right: -30,
  clipPath: "circle(50%)",
};

function useCategories(): CategoryCard[] {
  const t = useTranslations();
  return [
    {
      title: t("access.overview.categories.find_care_title"),
      subtitle: t("access.overview.categories.find_care_subtitle"),
      action: "access.overview.categories.find_care_title",
      texture: "gold",
      route: "FindCare",
    },
    {
      title: t("access.overview.categories.testing_title"),
      subtitle: t("access.overview.categories.testing_subtitle"),
      action: "access.overview.categories.testing_title",
      texture: "copper",
      route: "Testing",
    },
    {
      title: t("access.overview.categories.programs_title"),
      subtitle: t("access.overview.categories.programs_subtitle"),
      action: "access.overview.categories.programs_title",
      texture: "copper2",
      route: "Programs",
    },
    {
      title: t("access.overview.categories.supplements_title"),
      subtitle: t("access.overview.categories.supplements_subtitle"),
      action: "access.overview.categories.supplements_title",
      texture: "copper3",
      route: "Supplements",
    },
    {
      title: t("access.overview.categories.donor_registry_title"),
      subtitle: t("access.overview.categories.donor_registry_subtitle"),
      action: "access.overview.categories.donor_registry_title",
      texture: "gold",
      route: "DonorRegistry",
      statusBadge: "beta",
    },
    {
      title: t("access.overview.categories.therapies_title"),
      subtitle: t("access.overview.categories.therapies_subtitle"),
      action: "access.overview.categories.therapies_title",
      texture: "copper2",
      route: "Therapies",
    },
    {
      title: t("access.overview.categories.pharmacy_title"),
      subtitle: t("access.overview.categories.pharmacy_subtitle"),
      action: "access.overview.categories.pharmacy_title",
      texture: "gold",
      route: "Pharmacy",
    },
  ];
}

/* ─── Inject CSS shimmer keyframes (web only, once) ─── */

let cssInjected = false;
function injectShimmerCSS() {
  if (cssInjected || Platform.OS !== "web") return;
  cssInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes texture-drift {
      0%   { transform: translate(-30px, 20px); }
      25%  { transform: translate(20px, -10px); }
      50%  { transform: translate(30px, -20px); }
      75%  { transform: translate(-20px, 10px); }
      100% { transform: translate(-30px, 20px); }
    }
  `;
  document.head.appendChild(style);
}

function AnimatedHeroTexture() {
  const drift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(drift, {
        toValue: 1,
        duration: 8000,
        easing: Easing.inOut(Easing.sin),
        useNativeDriver: Platform.OS !== "web",
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [drift]);

  const translateX = drift.interpolate({
    inputRange: [0, 0.33, 0.66, 1],
    outputRange: [-22, 18, -6, -22],
  });
  const translateY = drift.interpolate({
    inputRange: [0, 0.33, 0.66, 1],
    outputRange: [-10, 8, 14, -10],
  });
  const scale = drift.interpolate({
    inputRange: [0, 0.33, 0.66, 1],
    outputRange: [1.02, 1.04, 1.03, 1.02],
  });

  const baseStyle = {
    position: "absolute",
    width: "132%",
    height: "132%",
    left: "-16%",
    top: "-16%",
  } as const;

  return (
    <Animated.Image
      source={COPPER_TEXTURE}
      style={{
        ...baseStyle,
        transform: [{ translateX }, { translateY }, { scale }],
      }}
      resizeMode="cover"
    />
  );
}

/* ─── Textured Shape ─── */

function TexturedShape({ shape }: { shape: ShapeConfig }) {
  // Native: Animated API for drift
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (Platform.OS === "web") {
      injectShimmerCSS();
      return;
    }
    const loop = Animated.loop(
      Animated.timing(shimmer, {
        toValue: 1,
        duration: 8000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const translateX = shimmer.interpolate({
    inputRange: [0, 0.25, 0.5, 0.75, 1],
    outputRange: [-30, 20, 30, -20, -30],
  });
  const translateY = shimmer.interpolate({
    inputRange: [0, 0.25, 0.5, 0.75, 1],
    outputRange: [20, -10, -20, 10, 20],
  });

  const webTransforms: string[] = [];
  if (shape.rotate != null) webTransforms.push(`rotate(${shape.rotate}deg)`);

  const isGold = shape.texture === "gold";

  return (
    <View
      style={
        {
          position: "absolute",
          width: shape.size,
          height: shape.size,
          borderRadius: Platform.OS === "web" ? 0 : shape.radius,
          overflow: "hidden",
          opacity: isGold ? 0.7 : 0.85,
          top: shape.top,
          right: shape.right,
          ...(Platform.OS === "web" && {
            clipPath: shape.clipPath,
            WebkitClipPath: shape.clipPath,
            ...(webTransforms.length > 0 && {
              transform: webTransforms.join(" "),
            }),
          }),
        } as Record<string, unknown>
      }
    >
      {Platform.OS === "web" ? (
        <Image
          source={TEXTURE_MAP[shape.texture ?? "copper"]}
          style={
            {
              width: isGold ? "300%" : "160%",
              height: isGold ? "300%" : "160%",
              position: "absolute",
              left: isGold ? "-80%" : "-30%",
              top: isGold ? "-60%" : "-30%",
              animation: "texture-drift 8s linear infinite",
            } as Record<string, unknown>
          }
          resizeMode="cover"
        />
      ) : (
        <Animated.Image
          source={TEXTURE_MAP[shape.texture ?? "copper"]}
          style={{
            width: isGold ? "300%" : "160%",
            height: isGold ? "300%" : "160%",
            position: "absolute",
            left: isGold ? "-80%" : "-30%",
            top: isGold ? "-60%" : "-30%",
            transform: [{ translateX }, { translateY }],
          }}
          resizeMode="cover"
        />
      )}
    </View>
  );
}

/* ─── Category Grid Card ─── */

function CategoryGridCard({
  card,
  onPress,
}: {
  card: CategoryCard;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("CategoryGridCard");
  const statusBadge =
    card.statusBadge ?? (card.comingSoon ? "coming-soon" : null);
  // Coming-soon categories are not yet ready for users; keep the card visible
  // (with its badge) but non-navigable so it can't open an unfinished screen.
  const isComingSoon = card.comingSoon === true;

  return (
    <Pressable
      onPress={
        isComingSoon
          ? undefined
          : () => {
              capturePress(pressLabel(card.title), card.action);
              onPress();
            }
      }
      disabled={isComingSoon}
      accessibilityRole="button"
      accessibilityLabel={card.title}
      accessibilityState={{ disabled: isComingSoon }}
      style={({ focused }) =>
        ({
          flex: 1,
          minWidth: "46%",
          borderRadius: 24,
          overflow: "hidden",
          backgroundColor: "rgba(255, 255, 255, 0.35)",
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.55)",
          opacity: isComingSoon ? 0.6 : 1,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.06,
          shadowRadius: 20,
          ...(Platform.OS === "web" && {
            backdropFilter: "blur(60px)",
            WebkitBackdropFilter: "blur(60px)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            ...(focused && {
              borderColor: EMBER.colors.warm,
              borderWidth: 2,
              outlineColor: EMBER.colors.warm,
              outlineStyle: "solid",
              outlineWidth: 2,
              outlineOffset: 2,
            }),
          }),
        }) as Record<string, unknown>
      }
    >
      {/* Abstract textured shape — clipped by card overflow */}
      <TexturedShape shape={{ ...CATEGORY_SHAPE, texture: card.texture }} />

      {/* Inner highlight for gloss */}
      <View
        style={{
          position: "absolute",
          top: 1,
          left: 1,
          right: 1,
          height: "50%",
          borderTopLeftRadius: 23,
          borderTopRightRadius: 23,
          borderTopWidth: 1,
          borderLeftWidth: 0.5,
          borderRightWidth: 0.5,
          borderColor: "rgba(255, 255, 255, 0.5)",
        }}
      />

      {/* Content — pinned to bottom-left */}
      <View
        style={{
          padding: 18,
          paddingBottom: 20,
          justifyContent: "flex-end",
          minHeight: 190,
        }}
      >
        <View style={{ gap: 3 }}>
          {statusBadge ? (
            <View style={{ alignSelf: "flex-start", marginBottom: 4 }}>
              <StatusBadge type={statusBadge} />
            </View>
          ) : null}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
              letterSpacing: -0.3,
            }}
          >
            {card.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12.5,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {card.subtitle}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

/* ─── Overview Screen ─── */

type OverviewProps = NativeStackScreenProps<
  AccessStackParamList,
  "AccessOverview"
>;

function AccessOverviewScreen({ navigation }: OverviewProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const capturePress = usePressCapture("AccessOverviewScreen");
  const categories = useCategories();
  // Re-resolve the featured block when the locale changes so the hero title/
  // subtitle stay localized after a live language switch (getBlocksByKind
  // applies the current locale at call time).
  const featuredProgram = useMemo(() => {
    void locale;
    return getBlocksByKind("program")[0];
  }, [locale]);
  const featuredTitle =
    featuredProgram?.title ?? t("access.overview.featured_fallback_title");
  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader
          title={t("access.overview.title")}
          subtitle={t("access.overview.subtitle")}
        />

        {/* Featured Hero Card */}
        <Pressable
          onPress={() => {
            // Content-catalogue title, so it varies per featured block.
            capturePress(pressLabel(featuredTitle), "featured_program");
            navigation.navigate("Programs");
          }}
          accessibilityRole="button"
          accessibilityLabel={featuredTitle}
          style={({ focused }) => ({
            borderRadius: 24,
            overflow: "hidden",
            height: 200,
            marginTop: 20,
            ...(Platform.OS === "web" &&
              focused && {
                borderColor: EMBER.colors.warm,
                borderWidth: 2,
                outlineColor: EMBER.colors.warm,
                outlineStyle: "solid",
                outlineWidth: 2,
                outlineOffset: 2,
              }),
          })}
        >
          <AnimatedHeroTexture />
          <View
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: 24,
              borderWidth: 0.5,
              borderColor: "rgba(255, 255, 255, 0.12)",
            }}
          />
          <View
            style={{
              flex: 1,
              padding: 24,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                alignSelf: "flex-start",
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 999,
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            >
              <Package
                size={11}
                color="rgba(255,255,255,0.7)"
                strokeWidth={2}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: "rgba(255,255,255,0.7)",
                  letterSpacing: 0.3,
                }}
              >
                {t("access.overview.featured_program_eyebrow")}
              </Text>
            </View>
            <View style={{ gap: 6 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 22,
                  fontWeight: "700",
                  color: "#FFFFFF",
                  lineHeight: 28,
                }}
              >
                {featuredTitle}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 19,
                }}
              >
                {featuredProgram?.subtitle ??
                  t("access.overview.featured_fallback_subtitle")}
              </Text>
            </View>
          </View>
        </Pressable>

        {/* Category Grid */}
        <View style={{ marginTop: 16, gap: 12 }}>
          {Array.from({ length: Math.ceil(categories.length / 2) }, (_, i) =>
            categories.slice(i * 2, i * 2 + 2),
          )
            .filter((row) => row.length > 0)
            .map((row, rowIdx) => (
              <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
                {row.map((cat) => (
                  <CategoryGridCard
                    key={cat.route}
                    card={cat}
                    onPress={() => navigation.navigate(cat.route)}
                  />
                ))}
                {row.length === 1 && <View style={{ flex: 1 }} />}
              </View>
            ))}
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}

/* ─── Navigator ─── */

export default function AccessNavigator() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="AccessOverview" component={AccessOverviewScreen} />
      <Stack.Screen name="FindCare" component={FindCareScreen} />
      <Stack.Screen name="ClinicDetail" component={ClinicDetailScreen} />
      <Stack.Screen
        name="NetworkLocations"
        component={NetworkLocationsScreen}
      />
      <Stack.Screen name="Testing" component={TestingScreen} />
      <Stack.Screen name="AmhComparison" component={AmhComparisonScreen} />
      <Stack.Screen name="TestComparison" component={AmhComparisonScreen} />
      <Stack.Screen
        name="TestPanelComparison"
        component={TestPanelComparisonScreen}
      />
      <Stack.Screen name="PgtComparison" component={PgtComparisonScreen} />
      <Stack.Screen
        name="EndometrialAssayComparison"
        component={EndometrialAssayComparisonScreen}
      />
      <Stack.Screen name="TestProductDetail" component={TestDetailScreen} />
      <Stack.Screen name="TestDetail" component={TestDetailScreen} />
      <Stack.Screen name="Programs" component={ProgramsScreen} />
      <Stack.Screen name="ProgramDetail" component={ProgramDetailScreen} />
      <Stack.Screen name="ConditionDetail" component={ConditionDetailScreen} />
      <Stack.Screen name="Supplements" component={SupplementsScreen} />
      <Stack.Screen
        name="SupplementDetail"
        component={SupplementDetailScreen}
      />
      <Stack.Screen name="DonorRegistry" component={DonorRegistryScreen} />
      <Stack.Screen name="Therapies" component={TherapiesScreen} />
      <Stack.Screen name="TherapyDetail" component={TherapyDetailScreen} />
      <Stack.Screen name="Pharmacy" component={PharmacyScreen} />
      <Stack.Screen name="PharmacyDetail" component={PharmacyDetailScreen} />
    </Stack.Navigator>
  );
}
